import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Home, Wallet, HandCoins, ArrowLeftRight, User, Bell, Headset } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Brand } from "@/components/Brand";
import { ThemeToggle } from "@/components/ThemeToggle";
import { api } from "@/lib/api";
import { useUnreadChat } from "@/lib/chat";
import { playChime, syncPush } from "@/lib/push";

const NAV = [
  { to: "/app", label: "Beranda", icon: Home, end: true, id: "home" },
  { to: "/app/tabungan", label: "Tabungan", icon: Wallet, id: "savings" },
  { to: "/app/pinjaman", label: "Pinjaman", icon: HandCoins, id: "loans" },
  { to: "/app/transaksi", label: "Transaksi", icon: ArrowLeftRight, id: "transactions" },
  { to: "/app/profil", label: "Profil", icon: User, id: "profile" },
];

export default function MobileShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  const chatUnread = useUnreadChat("/chat/unread");
  const isHome = location.pathname === "/app";

  const lastTs = useRef(null);
  const checkNew = async () => {
    const { data } = await api.get("/notifications");
    const fresh = lastTs.current ? data.filter((n) => !n.read && n.created_at > lastTs.current) : [];
    lastTs.current = data[0]?.created_at || lastTs.current || new Date().toISOString();
    fresh.reverse().forEach((n) => {
      const incoming = n.title === "Dana masuk";
      const toLoan = n.title === "Pengingat angsuran";
      const newLogin = n.kind === "new_login";
      toast[incoming ? "success" : toLoan || newLogin ? "warning" : "info"](n.title, {
        description: n.message, duration: newLogin ? 15000 : 8000,
        action: incoming ? { label: "Lihat", onClick: () => navigate("/app/transaksi") }
          : toLoan ? { label: "Bayar", onClick: () => navigate("/app/transaksi/bayar") }
          : newLogin ? { label: "Bukan Saya", onClick: () => navigate("/app/notifikasi") }
          : n.kind === "ticket" ? { label: "Lihat", onClick: () => navigate(`/app/bantuan/${n.ticket_id}`) } : undefined,
      });
    });
    if (fresh.length) playChime();
  };
  useEffect(() => {
    api.get("/me/summary").then((r) => setUnread(r.data.unread)).catch(() => {});
  }, [location.pathname]);
  useEffect(() => {
    syncPush();
    checkNew().catch(() => {});
    const t = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      api.get("/me/summary").then((r) => { setUnread(r.data.unread); return checkNew(); }).catch(() => {});
    }, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
  }, []);


  return (
    <div className="max-w-md mx-auto min-h-screen bg-slate-50 relative pb-28 sm:border-x border-slate-200 sm:shadow-2xl">
      <header className={`sticky top-0 z-30 bg-blue-800 px-4 py-2.5 flex items-center justify-between ${isHome ? "" : "shadow-md shadow-blue-900/20"}`}>
        <button onClick={() => navigate("/app")} data-testid="header-home-link"><Brand light /></button>
        <div className="flex items-center">
          <ThemeToggle light />
          <button onClick={() => navigate("/app/notifikasi")} data-testid="notification-bell" className="relative p-2 rounded-full hover:bg-white/15 transition-colors" aria-label="Notifikasi">
            <Bell className="h-6 w-6 text-white" />
            {unread > 0 && (
              <span data-testid="notification-unread-count" className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 ring-2 ring-blue-800 text-white text-[10px] font-bold flex items-center justify-center">
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </button>
          <button onClick={() => navigate("/app/chat")} data-testid="header-support" className="relative p-2 rounded-full hover:bg-white/15 transition-colors" aria-label="Chat Admin CU">
            <Headset className="h-6 w-6 text-white" />
            {chatUnread > 0 && (
              <span data-testid="chat-unread-count" className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-emerald-500 ring-2 ring-blue-800 text-white text-[10px] font-bold flex items-center justify-center">
                {chatUnread > 9 ? "9+" : chatUnread}
              </span>
            )}
          </button>
        </div>
      </header>
      <main className={isHome ? "" : "pt-3"}><Outlet /></main>
      <nav className="fixed bottom-0 inset-x-0 z-30 max-w-md mx-auto bg-gradient-to-b from-blue-800 to-blue-900 rounded-t-3xl shadow-[0_-6px_20px_rgba(30,58,138,0.35)] grid grid-cols-5 gap-1 px-2 pt-2 pb-[max(0.6rem,env(safe-area-inset-bottom))]">
        {NAV.map(({ to, label, icon: Icon, end, id }) => (
          <NavLink key={to} to={to} end={end} data-testid={`bottom-nav-${id}`}
            className={({ isActive }) => `flex flex-col items-center gap-1 py-2 rounded-2xl text-[11px] font-semibold text-white transition-colors ${isActive ? "bg-blue-500/60 ring-1 ring-white/20" : "hover:bg-white/10"}`}>
            <Icon className="h-5 w-5" />
            {label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
